import { type Static, Type as t } from '@sinclair/typebox';
import { CategorySchema } from '../game/categories';
import { LocaleSchema } from '../i18n/locale';
import { PlayerSchema, RoleSchema } from './player';

/**
 * Length of human-typed room codes. Single source of truth shared by the
 * server code generator (`newRoomCode`), the schema validator below, and
 * the web join form. Change here, propagate everywhere.
 */
export const ROOM_CODE_LENGTH = 3;

/**
 * Wire-compat shim. Undercover is the only mode; the field stays on the wire
 * as a literal so older snapshots/clients keep validating. Drop in a follow-up.
 */
export const UNDERCOVER_MODE_ID = 'infiltre-classic' as const;
export type UndercoverModeId = typeof UNDERCOVER_MODE_ID;

export const Phase = {
  Lobby: 'lobby',
  Reveal: 'reveal',
  Discuss: 'discuss',
  Vote: 'vote',
  RevealVote: 'reveal_vote',
  ClochardGuess: 'clochard_guess',
  Finished: 'finished',
} as const;
export type Phase = (typeof Phase)[keyof typeof Phase];

export const PhaseSchema = t.Union(
  [
    t.Literal(Phase.Lobby),
    t.Literal(Phase.Reveal),
    t.Literal(Phase.Discuss),
    t.Literal(Phase.Vote),
    t.Literal(Phase.RevealVote),
    t.Literal(Phase.ClochardGuess),
    t.Literal(Phase.Finished),
  ],
  { $id: 'Phase' },
);

export const RoomOptionsSchema = t.Object(
  {
    includeClochard: t.Boolean(),
    locale: LocaleSchema,
    categories: t.Array(CategorySchema, { minItems: 1 }),
    discussionSeconds: t.Integer({ minimum: 0, maximum: 600 }),
    /** Host-chosen; clamped at start to roster (≥1 Infiltré, ≥1 Flic post-Clochard). */
    infiltreCount: t.Integer({ minimum: 1, maximum: 10 }),
    /** Online: true (fairness). Standalone: false (table seating). Not client-overridable. */
    randomizeSpeakingOrder: t.Boolean(),
    /** Host-toggled. When true, room code is masked for everyone (host included). */
    hideCode: t.Optional(t.Boolean()),
  },
  { $id: 'RoomOptions' },
);
export type RoomOptions = Static<typeof RoomOptionsSchema>;

export const VoteSchema = t.Object({ voter: t.String(), target: t.String() }, { $id: 'Vote' });
export type Vote = Static<typeof VoteSchema>;

export const RoomSchema = t.Object(
  {
    id: t.String(),
    code: t.String({ minLength: ROOM_CODE_LENGTH, maxLength: ROOM_CODE_LENGTH }),
    modeId: t.Literal(UNDERCOVER_MODE_ID),
    phase: PhaseSchema,
    players: t.Array(PlayerSchema),
    options: RoomOptionsSchema,
    flicWord: t.Optional(t.String()),
    infiltreWord: t.Optional(t.String()),
    speakingOrder: t.Array(t.String()),
    currentSpeaker: t.Optional(t.String()),
    votes: t.Array(VoteSchema),
    /** Replay-vote opt-ins. Reset on `NextRound`. Eliminated players may opt in. */
    nextRoundRequests: t.Array(t.String()),
    /** Player ids who have sent AcknowledgeWord during the current Reveal phase.
     *  Reset when entering Reveal and when leaving Reveal (gates unanimous advance). */
    revealAcknowledgedBy: t.Array(t.String()),
    eliminatedThisRound: t.Optional(t.String()),
    winner: t.Optional(RoleSchema),
    round: t.Integer({ minimum: 0 }),
    createdAt: t.Number(),
    /** ms of last successful engine transition. Drives idle GC. */
    lastActivityAt: t.Number(),
    /** ms when entering `Finished`. Optional for legacy `localStorage:v2` snapshots. */
    finishedAt: t.Optional(t.Number()),
    /** ms of server-side discuss→vote auto-flip. Display-only on client. */
    discussDeadlineAt: t.Optional(t.Number()),
    /** Opaque key (e.g. `discord-instance:<id>`) for idempotent create-or-join. */
    externalKey: t.Optional(t.String({ maxLength: 128 })),
  },
  { $id: 'Room' },
);
export type Room = Static<typeof RoomSchema>;
